<template>
  <DragPanel
    title="二维图纸设置"
    :closePanel="closePanel"
    :tooltipVisible="false"
    ref="dom"
  >
    <div class="tips-box dialog-tip">
      <span class="tips-text">
        注：修改矢量切片精度后需重新转换才可生效，请耐心等待
      </span>
    </div>
    <div class="loading-box" v-if="isLoading">
      <Spin
        :pointNum="8"
        :translateX="25"
        background="var(--base-primary-bgColor)"
      />
    </div>
    <div class="cad-setting-box" v-else>
      <div class="item-box clear-margin">
        <div class="label-box dialog-label">贴地展示：</div>
        <div class="common-switch-box">
          <el-switch
            class="switch-primary"
            v-model="isTouchDown"
            @change="handleTouchDownChange"
          />
        </div>
      </div>
      <div class="item-box">
        <div class="label-box">
          <div class="label dialog-label">二维图纸切片精度</div>
          <el-tooltip
          :effect="$theme.tooltipKey()"
            placement="top-start"
            popper-class="common-panel-tooltip-box"
          >
            <div slot="content">
              根据二维图纸的覆盖范围计算得到的划分级别，该值越大，切分的二维图纸瓦片颗粒越精细，但是转换过程会更耗时，系统缺省值是较为平衡的方案，除非精度明显不足，否则不建议调整此值。
            </div>
            <div class="icon-box">
              <icon-svg icon-class="ic-jieshishuoming" class="icon" />：
            </div>
          </el-tooltip>
        </div>
      </div>
      <div class="common-slider-box">
        <el-slider
          class="slider-primary"
          input-size="small"
          v-model="currCadLod"
          :max="maxLod"
          :step="1"
          :disabled="isCurrLodDisabled()"
          :format-tooltip="(value) => formatCurrLodTooltip(value)"
        />
      </div>

      <div class="item-box" v-if="isTouchDown">
        <div class="label-box">
          <div class="label">最远可见LOD</div>
          <el-tooltip
          :effect="$theme.tooltipKey()"
            placement="top-start"
            popper-class="common-panel-tooltip-box"
          >
            <div slot="content">
              用于调整二维图纸在相机下降到哪个距离才可以看到，每个距离对应一个瓦片划分级别，该级别的最小值不能大于切片精度的最大值。
            </div>
            <div class="icon-box">
              <icon-svg icon-class="ic-jieshishuoming" class="icon" />：
            </div>
          </el-tooltip>
        </div>
      </div>
      <div class="common-slider-box" v-if="isTouchDown">
        <el-slider
          class="slider-primary"
          input-size="small"
          v-model="currVisibleDistance"
          :max="maxLod"
          :disabled="!(currVisibleDistance <= currCadLod)"
          :format-tooltip="(value) => formatLodTooltip(value)"
          @change="handleLodChange"
        />
      </div>
      <div class="item-box" v-if="isTouchDown && layerList.length">
        <div class="label-box">
          <div class="label">显示优先级调整</div>
          <el-tooltip
          :effect="$theme.tooltipKey()"
            placement="top-start"
            popper-class="common-panel-tooltip-box"
          >
            <div slot="content">
              注：矢量数据与二维图纸共用一套优先级排序系统，并规定所有二维图纸的显示优先级在矢量之上,
              在同一排序系统中按照加入场景的时间倒序显示，越靠后添加，优先级越高
            </div>
            <div class="icon-box">
              <icon-svg icon-class="ic-jieshishuoming" class="icon" />：
            </div>
          </el-tooltip>
        </div>
      </div>
      <div class="priority-setting-box" v-if="isTouchDown && layerList.length">
        <div class="layer-list-box">
          <div
            class="layer-item-box"
            v-for="(item, index) in layerList"
            :key="index"
          >
            <div class="text-overflow" :title="item.name">
              {{ item.name }}
            </div>
            <div class="common-button-box">
              <el-button
                type="text"
                size="small"
                title="上移"
                :disabled="index === 0"
                @click="moveLayer(item, true)"
              >
                <icon-svg icon-class="ic-shengxu" class="icon" />
              </el-button>
              <el-button
                type="text"
                size="small"
                title="下移"
                :disabled="index === layerList.length - 1"
                @click="moveLayer(item, false)"
              >
                <icon-svg icon-class="ic-jiangxu" class="icon" />
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="operation-btn-box">
      <el-button
        class="normal-ang-btn gr8 hollowOut-btn"
        size="small"
        @click="closePanel"
      >
        取消
      </el-button>
      <el-button
        class="primary-btn primary-ang-btn"
        size="small"
        type="primary"
        @click="submit"
      >
        确定
      </el-button>
    </div>
  </DragPanel>
</template>

<script>
import DragPanel from "@/components/CustomDragPanel.vue";
import Spin from "@/components/Spin.vue";

import {
  getSceneCadConfigService,
  updateSceneCadConfigService,
} from "@/service/interface";

export default {
  data() {
    return {
      isLoading: false,

      originConfig: {},

      isTouchDown: false, // 是否贴地
      currCadLod: 0,
      currVisibleDistance: 0,
      layerList: [],

      maxLod: 0,
    };
  },
  props: {
    cadInfo: Object,
    updateUnitLayerLevelByLayerList: Function,
    setUnitLODLevByDataSetId: Function,
    openConvertQueue: Function,
    updateSceneTreeStatus: Function,
    updateSceneTree: Function,
    closeCADSettingPanel: Function,
  },
  components: {
    DragPanel,
    Spin,
  },
  mounted() {
    this.initCadConfig().then(() => {
      this.setPanelStyle();
    });
  },
  methods: {
    initCadConfig() {
      this.isLoading = true;
      return new Promise((resolve) => {
        let cadInfo = this.cadInfo;
        let nodeId = cadInfo.sceneNodeId;
        getSceneCadConfigService(nodeId).then((response) => {
          if (response.isSuccess) {
            let data = response.data;
            this.originConfig = JSON.parse(JSON.stringify(data));

            let systemLodConfig = data.systemLodConfig;
            this.maxLod = Number(systemLodConfig.lodNum) - 1;

            this.isTouchDown = data.isTouchDown;
            this.currCadLod = data.lod;
            this.currVisibleDistance = data.visibleDistance;
            this.layerList = data.priorityList;

            this.isLoading = false;
            resolve();
          } else {
            this.$message.error(response.errMsg);
          }
        });
      });
    },
    handleTouchDownChange(isTouchDown) {
      let originConfig = this.originConfig;
      if (isTouchDown) {
        let touchDownConfig = originConfig.touchDownSystemLodConfig;
        this.maxLod = Number(touchDownConfig.lodNum) - 1;
        this.currCadLod = Number(touchDownConfig.recommendLod);
      } else {
        let systemLodConfig = originConfig.systemLodConfig;
        this.maxLod = Number(systemLodConfig.lodNum) - 1;
        this.currCadLod = Number(systemLodConfig.recommendLod);
      }

      this.setPanelStyle();
    },
    handleLodChange(value) {
      let cadInfo = this.cadInfo;
      let dataSetId = cadInfo.dataSetId;
      let range = [value, this.maxLod];
      this.setUnitLODLevByDataSetId(dataSetId, range);
    },
    moveLayer(layerInfo, isUpMove) {
      let findIndex = this.layerList.findIndex(
        (el) => el.treeNodeId === layerInfo.treeNodeId
      );
      let elementToMove = this.layerList[findIndex];
      this.layerList.splice(findIndex, 1);

      // 再将元素插入到新的位置
      if (isUpMove) {
        this.layerList.splice(findIndex - 1, 0, elementToMove);
      } else {
        this.layerList.splice(findIndex + 1, 0, elementToMove);
      }

      this.updateUnitLayerLevelByLayerList(this.layerList);
    },
    submit() {
      let cadInfo = this.cadInfo;
      let treeNodeId = cadInfo.sceneNodeId;
      let priorityList = this.layerList.map((el) => {
        let obj = { treeNodeId: el.treeNodeId };
        return obj;
      });

      let params = {
        treeNodeId,
        isTouchDown: this.isTouchDown,
        lod: this.currCadLod,
        visibleDistance: this.currVisibleDistance,
        priorityList,
      };
      updateSceneCadConfigService(params).then((response) => {
        if (response.isSuccess) {
          this.handleConfigChange(treeNodeId).then(() => {
            this.closePanel();

            this.$message.success("设置成功");
          });
        } else {
          this.$message.error(response.errMsg);
        }
      });
    },
    handleConfigChange() {
      return new Promise((resolve) => {
        let cadInfo = this.cadInfo;
        let dataSetId = cadInfo.dataSetId;

        let originConfig = this.originConfig;
        if (originConfig.lod !== this.currCadLod) {
          this.openConvertQueue();
          this.updateSceneTreeStatus([dataSetId]);
          resolve();
          return;
        }

        this.updateSceneTree().then(() => {
          if (originConfig.isTouchDown !== this.isTouchDown) {
            this.openConvertQueue();
            this.updateSceneTreeStatus([dataSetId]);
            resolve();
            return;
          }

          resolve();
        });
      });
    },
    setPanelStyle() {
      this.$nextTick(() => {
        let domRef = this.$refs.dom.$el;
        let panelHeaderDom = domRef.querySelector(".header-box");
        let panelContentDom = domRef.querySelector(".content");
        let height = panelHeaderDom.offsetHeight + panelContentDom.offsetHeight;

        domRef.style.height = `${height}px`;
        domRef.style.minHeight = `${height}px`;
      });
    },
    isCurrLodDisabled() {
      if (this.isTouchDown) {
        let touchDownConfig = this.originConfig.touchDownSystemLodConfig;
        let recommendLod = Number(touchDownConfig.recommendLod);
        if (this.currCadLod < recommendLod) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    formatLodTooltip(value) {
      if (value > this.currCadLod) {
        this.currVisibleDistance = this.currCadLod;
        return "最远可见LOD的下限不能大于二维图纸切片精度";
      }
    },
    formatCurrLodTooltip(value) {
      if (this.isTouchDown) {
        let touchDownConfig = this.originConfig.touchDownSystemLodConfig;
        let recommendLod = Number(touchDownConfig.recommendLod);
        if (value < recommendLod) {
          this.currCadLod = recommendLod;
          return "贴地模式下，二维图纸切片精度的下限不能小于推荐值";
        }
      }
    },
    closePanel() {
      let originConfig = this.originConfig;
      let priorityList = originConfig.priorityList;
      this.updateUnitLayerLevelByLayerList(priorityList);

      let systemLodConfig = originConfig.systemLodConfig;
      let minLod = originConfig.visibleDistance;
      let maxLod = Number(systemLodConfig.lodNum) - 1;
      let range = [minLod, maxLod];
      let cadInfo = this.cadInfo;
      let dataSetId = cadInfo.dataSetId;
      this.setUnitLODLevByDataSetId(dataSetId, range);

      this.closeCADSettingPanel();
    },
    // 打开其他面板前给出提示
    openOtherPanelCheck() {
      return new Promise((resolve) => {
        // prettier-ignore
        let message = '正在调整二维图纸设置，请结束后再执行相关操作';
        let options = {
          message,
          type: "warning",
          duration: 5000,
          showClose: true,
        };
        this.$message(options);
        resolve(false);
      });
    },
  },
};
</script>

<style lang="less" scoped>
@import "@/assets/css/publishSearch.less";
.common-drag-panel {
  left: 6px;
  top: 6px;
  width: 360px;
  height: unset;
  min-width: 360px;
  height: auto !important;

  .loading-box {
    height: 300px;
  }

  .tips-box {
    margin-top: 4px;
    margin-bottom: 0;
    padding-left: 0;
  }

  .cad-setting-box {
    padding: 15px;
    font-size: 14px;
    color: var(--default-color);

    .item-box {
      display: flex;
      align-items: center;
      margin-top: 15px;

      .label-box {
        display: flex;
        flex-shrink: 0;

        .icon-box {
          display: flex;
          align-items: center;
          margin-left: 3px;
        }

        .icon {
          margin-left: 5px;
        }
      }

      .common-switch-box {
        display: flex;

        .switch-name-box {
          margin-left: 10px;
        }
      }
    }

    .clear-margin {
      margin-top: 0px;
    }

    .common-slider-box {
      margin-top: 15px;
      padding: 3px 27px;
      border-radius: 8px;
      background: var(--scene-edit-setting-bgColor);
      border: 1px solid var(--scene-edit-setting-border-color);
    }

    .priority-setting-box {
      margin-top: 15px;
      border-radius: 8px;
      background-color: var(--scene-edit-setting-bgColor);
      border: 1px solid var(--scene-edit-setting-border-color);

      .layer-list-box {
        color: var(--default-color);
        padding: 10px;
        height: 120px;
        overflow-y: auto;

        .layer-item-box {
          display: flex;
          justify-content: space-between;
          padding: 0px 8px;
          border-radius: 4px;
          line-height: 32px;

          &:hover {
            background: var(--scene-edit-setting-item-hover-color);

            .common-button-box {
              display: block;

              // .el-button--small {
              //   padding: 5px 0px;
              // }
            }
          }

          .common-button-box {
            display: none;
            .icon {
              color: var(--icon-dark-color);
            }
          }
        }
      }
    }

    // .tips-box {
    //   margin-top: 15px;
    //   font-size: 12px;
    //   color: #aaaaaa;
    //   text-align: left;
    // }
  }

  .operation-btn-box {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 16px 16px;
    border-top: 1px solid var(--dialog-split-line-color);
  }
}
</style>
